import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import Model from "@tripian/model";
import { api } from "@tripian/core";

import ICombinedState from "../redux/model/ICombinedState";
import {
  changeLoadingPlans,
  changePlans,
  changeReference,
  saveNotification,
} from "../redux/action/trip";
import {
  adaptTimelineTripToReference,
  adaptTimelinePlansToPlan,
} from "../utils/timelineAdapters";

const useTimelineStep = () => {
  const tripHash = useSelector(
    (state: ICombinedState) => state.trip?.reference?.tripHash
  );
  const dispatch = useDispatch();

  /**
   * Refetch timeline after step operations
   */
  const refetchTimeline = useCallback(
    async (hash: string): Promise<Model.TimelineTrip> => {
      // retryCount=0 to disable polling
      return api
        .timeline(hash, 0, true, undefined, 0)
        .then((trip: Model.TimelineTrip) => {
          // Update Redux - use adaptTimelinePlansToPlan directly to preserve all steps
          const adaptedReference = adaptTimelineTripToReference(trip);
          const adaptedPlans = adaptTimelinePlansToPlan(trip.plans || []);
          dispatch(changeReference(adaptedReference));
          dispatch(changePlans(adaptedPlans));
          return trip;
        });
    },
    [dispatch]
  );

  /**
   * Add a POI step to timeline plan
   */
  const timelineStepAdd = useCallback(
    (
      planId: string,
      poiId: string,
      startTime?: string,
      endTime?: string
    ): Promise<any> => {
      dispatch(changeLoadingPlans(true));

      // Convert planId to number for API call
      const numericPlanId = parseInt(planId, 10);

      return api
        .timelineStepAdd(numericPlanId, poiId, startTime, endTime)
        .then((result: any) => {
          // Refetch timeline to get updated data
          return refetchTimeline(tripHash || "").then(() => result);
        })
        .catch(stepAddError => {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "timelineStepAdd",
              "Add Place To Timeline Plan",
              stepAddError
            )
          );
          throw stepAddError;
        })
        .finally(() => {
          dispatch(changeLoadingPlans(false));
        });
    },
    [dispatch, tripHash, refetchTimeline]
  );

  /**
   * Add a custom POI step to timeline plan
   */
  const timelineCustomStepAdd = useCallback(
    (
      planId: string,
      customPoi: Model.CustomPoi,
      stepType: string,
      startTime?: string,
      endTime?: string
    ): Promise<any> => {
      dispatch(changeLoadingPlans(true));

      // Convert planId to number for API call
      const numericPlanId = parseInt(planId, 10);

      return api
        .timelineCustomStepAdd(
          numericPlanId,
          customPoi,
          stepType,
          startTime,
          endTime
        )
        .then((result: any) => {
          // Refetch timeline to get updated data
          return refetchTimeline(tripHash || "").then(() => result);
        })
        .catch(stepAddError => {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "timelineCustomStepAdd",
              "Add Custom Place To Timeline Plan",
              stepAddError
            )
          );
          throw stepAddError;
        })
        .finally(() => {
          dispatch(changeLoadingPlans(false));
        });
    },
    [dispatch, tripHash, refetchTimeline]
  );

  /**
   * Replace a step with an alternative POI in timeline plan
   */
  const timelineStepReplace = useCallback(
    (stepId: number, alternativePoiId: string): Promise<any> => {
      dispatch(changeLoadingPlans(true));

      return api
        .timelineStepReplace(stepId, alternativePoiId)
        .then((result: any) => {
          // Refetch timeline to get updated data
          return refetchTimeline(tripHash || "").then(() => result);
        })
        .catch(stepReplaceError => {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "timelineStepReplace",
              "Replace Place in Timeline Plan",
              stepReplaceError
            )
          );
          throw stepReplaceError;
        })
        .finally(() => {
          dispatch(changeLoadingPlans(false));
        });
    },
    [dispatch, tripHash, refetchTimeline]
  );

  /**
   * Update step times in timeline plan
   */
  const timelineStepUpdateTimes = useCallback(
    (
      stepId: number,
      startTime: string,
      endTime: string
    ): Promise<any> => {
      dispatch(changeLoadingPlans(true));

      return api
        .timelineStepUpdateTimes(stepId, startTime, endTime)
        .then((result: any) => {
          // Refetch timeline to get updated data
          return refetchTimeline(tripHash || "").then(() => result);
        })
        .catch(stepUpdateTimesError => {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "timelineStepUpdateTimes",
              "Update Step Times in Timeline Plan",
              stepUpdateTimesError
            )
          );
          throw stepUpdateTimesError;
        })
        .finally(() => {
          dispatch(changeLoadingPlans(false));
        });
    },
    [dispatch, tripHash, refetchTimeline]
  );

  /**
   * Delete a step from timeline plan
   */
  const timelineStepDelete = useCallback(
    (stepId: number): Promise<any> => {
      dispatch(changeLoadingPlans(true));

      return api
        .timelineStepDelete(stepId)
        .then((result: any) => {
          // Refetch timeline to get updated data
          return refetchTimeline(tripHash || "").then(() => result);
        })
        .catch(stepDeleteError => {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "timelineStepDelete",
              "Delete Step from Timeline Plan",
              stepDeleteError
            )
          );
          throw stepDeleteError;
        })
        .finally(() => {
          dispatch(changeLoadingPlans(false));
        });
    },
    [dispatch, tripHash, refetchTimeline]
  );

  return {
    timelineStepAdd,
    timelineCustomStepAdd,
    timelineStepReplace,
    timelineStepUpdateTimes,
    timelineStepDelete,
  };
};

export default useTimelineStep;
